// EXPANDABLE_DRAWER_IMPLEMENTED_2026_02_28
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import MarkdownIt from 'markdown-it';
import { opportunityApi, aiApi, watchListApi, pipelineApi, documentApi, analyticsApi, agentApi } from '../../services/api';
import { formatAgency } from '../../utils/agency';
import { sourceInfo, sourceSiteName } from '../../utils/source';
import { formatLocation } from '../../utils/location';
import { useAuth } from '../../hooks/useAuth';
import { planAllows } from '../../config/navigation';
import AgentResultView, { extractAgentText } from '../../components/agentResults/AgentResultView';
import toast from 'react-hot-toast';
import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { ConversationHistory } from '../../components/chat/ConversationHistory';
import MatchScoreHints from '../../components/scoring/MatchScoreHints';
import { EligibilityGate } from '../../components/proposal/EligibilityGate';
import { SolicitationTypeBadge } from '../../components/proposal/SolicitationTypeBadge';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { 
  BuildingOfficeIcon, DocumentTextIcon,
  UserIcon, LinkIcon, MapPinIcon,
  StarIcon, ChatBubbleLeftRightIcon,
  PaperAirplaneIcon, SparklesIcon, DocumentIcon, ArrowDownTrayIcon,
  ChartBarIcon, EyeIcon, CheckIcon, BookmarkIcon,
  CloudIcon,
  ClockIcon,
  MicrophoneIcon,
  StopIcon,
  TagIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LockClosedIcon
} from 'components/icons';

interface Opportunity {
  id: string;
  title: string;
  agency: string;
  subAgency?: string;
  agencyHierarchy?: {
    department?: string;
    subTier?: string;
    majorCommand?: string;
    subCommand1?: string;
    subCommand2?: string;
    subCommand3?: string;
    office?: string;
  };
  solicitationNumber?: string;
  contractType: string;
  type?: string;
  baseType?: string;
  setAside?: string;
  setAsideCode?: string;
  value: number;
  naicsCode: string;
  naicsCodes?: string[];
  postedDate: string;
  responseDeadline: string;
  archiveDate?: string;
  archiveType?: string;
  status: string;
  active?: string;
  description: string;
  fullDescription?: string;
  classificationCode?: string;
  noticeId?: string;
  fullParentPathName?: string;
  fullParentPathCode?: string;
  placeOfPerformance?: {
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  office?: string;
  uiLink?: string;
  ui_link?: string;  // Database field name fallback
  /** "sam" (default) or "fl_vbs" — Florida Vendor Bid System. */
  source?: string;
  links?: string[];
  resourceLinks?: Array<{ url?: string; description?: string }>;
  pointOfContact?: Array<{
    title?: string;
    fullName?: string;
    email?: string;
    phone?: string;
    fax?: string;
    type?: string;
  }>;
  attachments?: Array<{ name: string; size?: number; url?: string }>;
  award?: {
    date?: string;
    number?: string;
    amount?: number;
    awardee?: { name?: string; ueiSAM?: string };
  };
  // Additional CSV fields
  subTier?: string;
  fpdsCode?: string;
  organizationType?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
}

// Note: ChatMessage is now imported from useChat hook

const SAMPLE_QUESTIONS = [
  "What are the key requirements for this contract?",
  "Is my company eligible to bid?",
  "What's the evaluation criteria?",
  "What documents do I need to submit?",
];

const TOPICS = [
  { id: 'general', label: 'General Information', icon: DocumentTextIcon },
  { id: 'compliance', label: 'Compliance', icon: ChartBarIcon },
  { id: 'pricing', label: 'Pricing', icon: TagIcon },
  { id: 'subcontracting', label: 'Subcontracting', icon: BuildingOfficeIcon },
  { id: 'submission', label: 'Bid Submission', icon: PaperAirplaneIcon },
  { id: 'proposal', label: 'Proposal Writing', icon: DocumentIcon },
];

// Markdown renderer instance
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
});

// Strip LLM tool-call control markers from streamed agent output so they don't
// leak into the visible chat bubble. Without this, Kimi/Moonshot format tokens
// like `<|tool_calls_section_begin|>{"agent":"document-reader", ...}` render as
// long unbroken strings that force horizontal page scroll on mobile.
function cleanAgentOutput(content: string): string {
  if (!content) return '';
  let cleaned = content;
  // Remove entire tool-call blocks — Kimi/Moonshot format
  cleaned = cleaned.replace(/<\|tool_calls_section_begin\|>[\s\S]*?<\|tool_calls_section_end\|>/g, '');
  // Remove stray individual markers (partial streams)
  cleaned = cleaned.replace(/<\|tool_call_begin\|>[\s\S]*?<\|tool_call_end\|>/g, '');
  cleaned = cleaned.replace(/<\|tool_call_argument_begin\|>/g, '');
  cleaned = cleaned.replace(/<\|tool_calls_section_begin\|>/g, '');
  cleaned = cleaned.replace(/<\|tool_calls_section_end\|>/g, '');
  cleaned = cleaned.replace(/<\|tool_call_begin\|>/g, '');
  cleaned = cleaned.replace(/<\|tool_call_end\|>/g, '');
  // Remove inline Kimi function-call labels like `functions.sessions_spawn:0`
  cleaned = cleaned.replace(/\bfunctions\.[a-z_][a-z0-9_]*:\d+\b/gi, '');
  return cleaned.trim();
}

// Small compact key/value pair for the Overview facts grid. `truncate` keeps
// long agency strings from stacking into multi-line paragraphs on mobile.
const Fact: React.FC<{
  label: string;
  value: string;
  title?: string;
  mono?: boolean;
  truncate?: boolean;
}> = ({ label, value, title, mono, truncate }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wide text-content-muted font-semibold">{label}</p>
    <p
      className={`text-xs sm:text-sm text-content font-medium ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : ''}`}
      title={title ?? value}
    >
      {value}
    </p>
  </div>
);

// Helper component to render markdown — strips tool markers + protects the
// mobile layout with aggressive word-break rules so long URLs/paths wrap
// inside the chat bubble instead of widening the whole page.
const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const html = useMemo(() => sanitizeHtml(md.render(cleanAgentOutput(content || ''))), [content]);
  return (
    <div
      className="markdown-content"
      style={{
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const OpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  // Same gate the sidebar applies to Proposal Writer (plan: 'PRO').
  const canBuildProposal = planAllows('PRO', user?.plan || 'FREE');
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aiSummary, setAiSummary] = useState<string>('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Chat state with harness WebSocket streaming
  const {
    messages: chatMessages,
    isLoading: isChatLoading,
    error: chatError,
    sendMessage: sendChatMessage,
    loadConversation,
    clearChat,
    creditsUsed,
    creditsTotal,
    isConnected: isChatConnected,
    transport: chatTransport,
    conversationId: chatConversationId,
  } = useChatWebSocket();
  
  // Conversation history sidebar state
  const [showHistory, setShowHistory] = useState(false);
  
  // Chat expanded drawer state
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  // Lock body scroll when drawer is expanded (prevents mobile viewport shrink on close)
  useEffect(() => {
    if (isChatExpanded) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }, [isChatExpanded]);
  
  // Selected topic for agent routing
  const [selectedTopic, setSelectedTopic] = useState<string>('general');
  // Topics block collapse state — default open so users don't miss it on first arrival.
  const [isTopicsExpanded, setIsTopicsExpanded] = useState<boolean>(true);
  // Sample questions block collapse state — auto-collapses after first user
  // message, auto-expands back when chat history is cleared.
  const [isSamplesExpanded, setIsSamplesExpanded] = useState<boolean>(true);

  // Auto-collapse on first message, auto-expand when messages return to zero
  // (e.g. after clear-history or deleting the active conversation).
  useEffect(() => {
    if (chatMessages.length === 0) {
      setIsSamplesExpanded(true);
    } else if (chatMessages.length === 1) {
      setIsSamplesExpanded(false);
    }
  }, [chatMessages.length]);

  // When the user deletes a conversation in the history panel, reset the chat
  // only if it was the one they were actively viewing. Otherwise leave the
  // current view untouched.
  const handleConversationDeleted = (deletedId: string) => {
    if (deletedId === chatConversationId) {
      clearChat();
    }
  };
  
  // Voice input
  const {
    isListening,
    transcript: voiceTranscript,
    interimTranscript,
    error: voiceError,
    isSupported: isVoiceSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition();
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  // Watch List & Pipeline state
  const [isInWatchList, setIsInWatchList] = useState(false);
  const [isInPipeline, setIsInPipeline] = useState(false);
  const [pipelineEntry, setPipelineEntry] = useState<{ id: string; stage: string } | null>(null);
  const [watchListLoading, setWatchListLoading] = useState(false);
  
  // Match score state
  const [matchScore, setMatchScore] = useState<number | null>(null);
  const [matchReasons, setMatchReasons] = useState<string[]>([]);
  const [matchPillars, setMatchPillars] = useState<{ quality: number; profileMatch: number; behavioralFit: number } | null>(null);
  const [matchWeights, setMatchWeights] = useState<{ quality: number; profileMatch: number; behavioralFit: number } | null>(null);

  // Agent insights state
  const [agentResults, setAgentResults] = useState<any[]>([]);
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);
  const [showStageMenu, setShowStageMenu] = useState(false);
  const stageMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showStageMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (stageMenuRef.current && !stageMenuRef.current.contains(e.target as Node)) {
        setShowStageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showStageMenu]);

  // Document filenames state
  const [documentFilenames, setDocumentFilenames] = useState<Record<string, string>>({});
  const [savedDocuments, setSavedDocuments] = useState<Set<string>>(new Set());
  const [savingDocument, setSavingDocument] = useState<string | null>(null);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    fetchOpportunityDetail();
    checkWatchListStatus();
    checkPipelineStatus();
    checkSavedDocuments();
    loadOpportunityConversation();
    fetchMatchScore();

    fetchAgentResults();

    // Track opportunity view
    analyticsApi.track('opportunity.viewed', { opportunityId: id });

    // Track time spent on unmount
    const startTime = Date.now();
    return () => {
      const timeSpentMs = Date.now() - startTime;
      if (timeSpentMs > 3000) { // Only track if > 3 seconds (avoid accidental navigations)
        analyticsApi.track('opportunity.time_spent', { opportunityId: id, timeSpentMs });
      }
    };
  // Keyed on the opportunity id. Every loader listed here is redefined per render, so including them would refetch the whole page continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchOpportunityDetail = async () => {
    try {
      setLoading(true);
      const response = await opportunityApi.getById(id!);
      const oppData = response.data.opportunity;
      setOpportunity(oppData);
      // Page can render now — the heavy bits (filenames, AI summary) load in
      // their own loading states. Holding the whole page on summary turned
      // uncached opps into 20-30s blank screens.
      setLoading(false);

      const parsedLinks = parseLinks(oppData?.links);
      const parsedResourceLinks = parseLinks(oppData?.resourceLinks);
      const allLinks = [
        ...parsedLinks.map(normalizeUrl).filter(Boolean),
        ...parsedResourceLinks.map(normalizeUrl).filter(Boolean)
      ] as string[];

      // Fire-and-forget: filenames
      if (allLinks.length > 0) {
        documentApi.getFilenames(allLinks)
          .then(filenamesRes => {
            const filenameMap: Record<string, string> = {};
            filenamesRes.data.data.forEach((item: { url: string; filename: string }) => {
              filenameMap[item.url] = item.filename;
            });
            setDocumentFilenames(filenameMap);
          })
          .catch(err => {
            console.error('Failed to fetch document filenames:', err);
          });
      }

      // Fire-and-forget: AI summary (uncached path can take 20-30s — UI shows
      // a skeleton in that section, rest of the page is interactive).
      setLoadingSummary(true);
      opportunityApi.getSummary(id!)
        .then(summaryRes => {
          const summary = summaryRes.data.summary;
          setAiSummary(typeof summary === 'string' ? summary : String(summary || ''));
        })
        .catch((err: any) => {
          console.error('Failed to fetch AI summary:', err.response?.data || err.message);
          if (oppData?.description) {
            setAiSummary(`<h3>Summary of Key Points</h3><ul><li>${oppData.title}</li><li>${oppData.setAside || 'Open to all businesses'}</li><li>NAICS Code: ${oppData.naicsCode || 'N/A'}</li></ul><h3>Suggestions</h3><ul><li>Review the full solicitation document</li><li>Verify eligibility requirements</li><li>Prepare proposal before deadline</li></ul>`);
          }
        })
        .finally(() => {
          setLoadingSummary(false);
        });
    } catch (err: any) {
      setError(err?.response?.status === 404
        ? 'This solicitation is not in our data and SAM.gov no longer returns it. It may have been removed or the link may be out of date.'
        : 'Failed to load opportunity details');
      console.error('Error fetching opportunity:', err);
      setLoading(false);
    }
  };

  // Fetch agent results for this opportunity
  const fetchAgentResults = async () => {
    if (!id) return;
    try {
      const response = await agentApi.getTasksByOpportunity(id);
      const tasks = response.data.data?.tasks || [];
      setAgentResults(tasks.filter((t: any) => t.status === 'completed' && t.result));
    } catch {
      // Silently fail
    }
  };

  // Fetch real match score from backend
  const fetchMatchScore = async () => {
    if (!id) return;
    try {
      const response = await pipelineApi.calculateMatch(id);
      const data = response.data.data;
      setMatchScore(data.score);
      setMatchReasons(data.reasons || []);
      setMatchPillars(data.pillars || null);
      setMatchWeights(data.weights || null);
    } catch (err) {
      // Silently fail - score will show as '--'
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !opportunity || isChatLoading) return;

    // Build SAM.gov URL
    const samUrl = opportunity.uiLink || `https://sam.gov/opp/${opportunity.noticeId || opportunity.id}/view`;

    // Collect all document download URLs for this opportunity
    const allDocUrls: string[] = [];
    const parsedResLinks = parseLinks(opportunity?.resourceLinks);
    const parsedDocLinks = parseLinks(opportunity?.links);
    for (const link of [...parsedResLinks, ...parsedDocLinks]) {
      const url = normalizeUrl(link);
      if (url) allDocUrls.push(url);
    }
    if (opportunity?.attachments) {
      for (const att of opportunity.attachments) {
        if (att.url && att.url !== '#') allDocUrls.push(att.url);
      }
    }

    // Build context from opportunity with topic for agent routing
    const context = {
      opportunityId: opportunity.id,
      opportunity: {
        title: opportunity.title,
        description: opportunity.description,
        agency: opportunity.agency,
        setAside: opportunity.setAside,
        naicsCode: opportunity.naicsCode,
        deadline: opportunity.responseDeadline,
        solicitationNumber: opportunity.solicitationNumber,
        noticeId: opportunity.noticeId || opportunity.id,
        ulink: samUrl,
        sourceUrl: samUrl,
        contractType: opportunity.contractType,
        placeOfPerformance: formatLocation(opportunity.placeOfPerformance, { zip: true }) || 'Not stated',
        estimatedValue: opportunity.value ? `$${opportunity.value.toLocaleString()}` : 'N/A',
        documentUrls: allDocUrls,
      },
      topic: selectedTopic !== 'general' ? selectedTopic : undefined,
    };

    const message = chatInput;
    setChatInput('');
    await sendChatMessage(message, context);
  };

  const handleSampleQuestion = (question: string) => {
    setChatInput(question);
  };

  // Sync voice transcript with chat input
  useEffect(() => {
    if (voiceTranscript) {
      setChatInput(voiceTranscript);
    }
  }, [voiceTranscript]);

  // Handle voice send - stop listening and send message
  const handleVoiceSend = () => {
    stopListening();
    if (voiceTranscript.trim()) {
      setChatInput(voiceTranscript);
      // Small delay to ensure state is updated
      setTimeout(() => {
        handleSendMessage();
        resetTranscript();
      }, 100);
    }
  };

  // Check if opportunity is in watch list
  const checkWatchListStatus = async () => {
    try {
      const response = await watchListApi.checkStatus(id!);
      setIsInWatchList(response.data.data.isInWatchList);
    } catch (err) {
      // Silently fail - user might not be logged in
    }
  };

  // Check if opportunity is in pipeline
  const checkPipelineStatus = async () => {
    try {
      const response = await pipelineApi.getByOpportunityId(id!);
      const entry = response.data.data;
      setIsInPipeline(!!entry);
      if (entry) {
        setPipelineEntry({ id: entry.id, stage: entry.stage });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setIsInPipeline(false);
        setPipelineEntry(null);
      }
    }
  };

  // Run an agent action directly from the detail page
  const runAgentFromDetail = async (agentType: string, actionName: string) => {
    if (!opportunity) {
      toast.error('Opportunity data not loaded');
      return;
    }
    try {
      toast.loading(`Starting ${actionName}...`, { id: `detail-${agentType}` });
      const response = await agentApi.createTask({
        agent_type: agentType,
        task: {
          opportunity_id: pipelineEntry?.id || id,
          opportunity_title: opportunity.title || 'Unknown',
        },
        priority: 'normal',
      });
      console.log('Agent task response:', response.data);
      toast.success(`${actionName} started — check notifications when complete`, { id: `detail-${agentType}` });
    } catch (error: any) {
      console.error('Agent task error:', error.response?.status, error.response?.data, error.message);
      const msg = error.response?.data?.error?.message || error.response?.data?.error || error.message || `Failed to start ${actionName}`;
      toast.error(typeof msg === 'string' ? msg : `Failed to start ${actionName}`, { id: `detail-${agentType}` });
    }
  };

  const handleStageChangeFromDetail = async (newStage: string) => {
    if (!pipelineEntry) return;
    try {
      await pipelineApi.updateStage(pipelineEntry.id, { stage: newStage });
      setPipelineEntry(prev => prev ? { ...prev, stage: newStage } : null);
      toast.success(`Moved to ${newStage.charAt(0).toUpperCase() + newStage.slice(1)}`);
    } catch {
      toast.error('Failed to update stage');
    }
  };

  // Check which documents are already saved to AI Document Hub
  const checkSavedDocuments = async () => {
    if (!id) return;
    try {
      const response = await aiApi.checkOpportunityDocuments(id);
      const savedUrls = response.data.data.savedUrls || [];
      setSavedDocuments(new Set(savedUrls));
    } catch (err) {
      // Silently fail - user might not be logged in or on free plan
    }
  };

  // Load existing conversation for this opportunity
  const loadOpportunityConversation = async () => {
    if (!id) {
      console.log('[ChatHistory] No opportunity ID, skipping');
      return;
    }
    try {
      console.log('[ChatHistory] Fetching conversations for opportunity:', id);
      // Fetch all user conversations
      const response = await aiApi.getConversations();
      const conversations = response.data.data || [];
      console.log('[ChatHistory] Total conversations:', conversations.length);
      
      // Find conversations for this opportunity
      const oppConversations = conversations.filter(
        (c: any) => {
          console.log('[ChatHistory] Checking conversation:', c.id, 'oppId:', c.opportunityId, 'match:', c.opportunityId === id);
          return c.opportunityId === id;
        }
      );
      
      console.log('[ChatHistory] Found conversations for this opportunity:', oppConversations.length);
      
      if (oppConversations.length > 0) {
        // Sort by updatedAt (most recent first)
        oppConversations.sort((a: any, b: any) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        // Load the most recent conversation
        const mostRecent = oppConversations[0];
        console.log('[ChatHistory] Loading most recent conversation:', mostRecent.id);
        await loadConversation(mostRecent.id);
        console.log('[ChatHistory] Conversation loaded successfully');
      } else {
        console.log('[ChatHistory] No conversations found for this opportunity');
      }
    } catch (err) {
      // Silently fail - user might not be logged in or on free plan
      console.error('[ChatHistory] Error loading conversation:', err);
    }
  };

  // Add to Watch List
  const handleAddToWatchList = async () => {
    setWatchListLoading(true);
    try {
      await watchListApi.add(id!);
      setIsInWatchList(true);
      showActionMessage('success', 'Added to Watch List');
    } catch (err: any) {
      if (err.response?.status === 409) {
        setIsInWatchList(true);
        showActionMessage('success', 'Already in Watch List');
      } else if (err.response?.data?.error?.message) {
        showActionMessage('error', err.response.data.error.message);
      } else {
        showActionMessage('error', 'Failed to add to Watch List');
      }
    } finally {
      setWatchListLoading(false);
    }
  };

  // Remove from Watch List
  const handleRemoveFromWatchList = async () => {
    setWatchListLoading(true);
    try {
      await watchListApi.remove(id!);
      setIsInWatchList(false);
      showActionMessage('success', 'Removed from Watch List');
    } catch (err) {
      showActionMessage('error', 'Failed to remove from Watch List');
    } finally {
      setWatchListLoading(false);
    }
  };

  // Add to Pipeline
  const handleAddToPipeline = async () => {
    setPipelineLoading(true);
    try {
      await pipelineApi.addOpportunity(id!);
      setIsInPipeline(true);
      // Adding creates the pursuit's workspace; land there.
      navigate(`/pursuits/${id}`);
      return;
    } catch (err: any) {
      if (err.response?.status === 409) {
        setIsInPipeline(true);
        showActionMessage('success', 'Already in Pipeline');
      } else if (err.response?.data?.error?.message) {
        showActionMessage('error', err.response.data.error.message);
      } else {
        showActionMessage('error', 'Failed to add to Pipeline');
      }
    } finally {
      setPipelineLoading(false);
    }
  };

  // Show action message
  const showActionMessage = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage(null), 3000);
  };

  // Save document to AI Document Hub
  const handleSaveDocument = async (url: string, filename: string) => {
    if (!opportunity) return;
    
    setSavingDocument(url);
    try {
      await aiApi.saveDocumentFromOpportunity({
        documentUrl: url,
        filename: filename,
        noticeId: opportunity.noticeId || opportunity.id,
        solicitationNumber: opportunity.solicitationNumber,
        title: opportunity.title,
        agency: opportunity.agency,
        source: 'opportunity',
      });
      
      setSavedDocuments(prev => new Set(prev).add(url));
      // Toast, not showActionMessage: that renders up by the tab bar, while the
      // save icons sit far down the page. Scrolled to the documents list, the
      // user never sees it and the click reads as a dead button.
      toast.success(`Saved "${filename}" to AI Document Hub`);
    } catch (err: any) {
      if (err.response?.status === 403) {
        toast.error('Saving to the AI Document Hub requires the Pro plan or higher.');
      } else {
        toast.error('Failed to save document');
      }
    } finally {
      setSavingDocument(null);
    }
  };

  // Check if description is meaningful (not a URL or empty)
  const hasMeaningfulDescription = (desc?: string): boolean => {
    if (!desc) return false;
    if (desc.trim().length < 20) return false;
    if (desc.startsWith('http://') || desc.startsWith('https://')) return false;
    return true;
  };

  // Parse links from opportunity (handles JSON string, array of strings, or array of objects)
  const parseLinks = (linksData: any): (string | { url?: string; description?: string })[] => {
    if (!linksData) return [];
    if (Array.isArray(linksData)) return linksData;
    if (typeof linksData === 'string') {
      try {
        const parsed = JSON.parse(linksData);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };
  
  // Normalize URL - handles both string URLs and objects with url property
  const normalizeUrl = (link: any): string | null => {
    if (!link) return null;
    if (typeof link === 'string') return link;
    if (typeof link === 'object' && link.url) return link.url;
    return null;
  };

  // Get document name - uses fetched filename from API or extracts from URL
  const getDocumentNameFromUrl = (url: any): string => {
    // Handle non-string inputs
    if (!url || typeof url !== 'string') return 'Document';
    
    // First check if we have the actual filename from the API
    if (documentFilenames[url]) {
      return documentFilenames[url];
    }
    
    // Florida VBS names files by a query parameter; the filenames API fills in the real name.
    const vbsMatch = url.match(/[?&]attachmentId=(\d+)/i);
    if (vbsMatch) return `Attachment ${vbsMatch[1]}`;

    // Fallback: Try to extract file ID from SAM.gov API URLs
    // Pattern: https://sam.gov/api/prod/opps/v3/opportunities/resources/files/{fileId}/download
    const samGovMatch = url.match(/\/files\/([a-f0-9-]+)\/download$/i);
    if (samGovMatch) {
      const fileId = samGovMatch[1];
      // Return shortened file ID (first 8 chars)
      return `File ${fileId.substring(0, 8)}...`;
    }
    
    // Try to extract filename from URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.length > 0) {
        // Remove query params if any
        return filename.split('?')[0];
      }
    } catch {
      // Invalid URL, fall through
    }
    
    // Fallback: return shortened URL
    return url.length > 40 ? url.substring(0, 40) + '...' : url;
  };

  // Where this notice lives. The link and the "view on …" copy name it.
  const sourceName = sourceSiteName(opportunity?.source);

  // Get SAM.gov URL from opportunity
  const getSamGovUrl = (): string => {
    // Use uiLink or ui_link from database
    if (opportunity?.uiLink) return opportunity.uiLink;
    if (opportunity?.ui_link) return opportunity.ui_link;
    // Fallback to constructed URL
    return `https://sam.gov/opp/${opportunity?.noticeId || opportunity?.id || ''}/view`;
  };

  const documentLinks = parseLinks(opportunity?.links);
  const resourceLinks = parseLinks(opportunity?.resourceLinks);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const getDaysRemaining = (deadline?: string) => {
    if (!deadline) return null;
    const end = new Date(deadline);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
    </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error || 'Opportunity not found'}</p>
        <Link to="/opportunities" className="mt-4 text-brand-600 hover:text-brand-700">← Back to Opportunities</Link>
    </div>
    );
  }

  const daysRemaining = getDaysRemaining(opportunity.responseDeadline);

  return (
    <div className="min-h-screen bg-surface-sunk">
      {/* Breadcrumb -->
      <div className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-content-muted">
            <Link to="/opportunities" className="hover:text-brand-600 flex items-center gap-1">
              <ArrowLeftIcon className="h-4 w-4" /> Opportunities
            </Link>
            <span>/</span>
            <span className="text-content font-medium truncate">{typeof opportunity.solicitationNumber === 'string' ? opportunity.solicitationNumber : (typeof opportunity.id === 'string' ? opportunity.id : 'Detail')}</span>
          </div>
        </div>
    </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Top Navigation Tabs */}
        <div className="bg-surface rounded-lg shadow-sm border border-border mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-border gap-3">
            <div className="flex items-center gap-3 sm:gap-6 overflow-x-auto">
              <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap ${activeTab === 'overview' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-content-muted hover:text-content'}`}>
                <DocumentTextIcon className="h-4 w-4" /> Overview
              </button>
              <button onClick={() => setActiveTab('general')} className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap ${activeTab === 'general' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-content-muted hover:text-content'}`}>
                <BuildingOfficeIcon className="h-4 w-4" /> General
              </button>
              <button onClick={() => setActiveTab('compliance')} className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap ${activeTab === 'compliance' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-content-muted hover:text-content'}`}>
                <ChartBarIcon className="h-4 w-4" /> Match Score
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Action Message Toast */}
              {actionMessage && (
                <div className={`px-3 py-1.5 rounded-lg text-sm ${actionMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {actionMessage.text}
                </div>
              )}
              
              {/* Watch List Button */}
              <button 
                onClick={isInWatchList ? handleRemoveFromWatchList : handleAddToWatchList}
                disabled={watchListLoading}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                  isInWatchList 
                    ? 'text-amber-600 border-amber-300 bg-amber-50 hover:bg-amber-100' 
                    : 'text-content-muted hover:text-content border-border-strong hover:bg-surface-sunk'
                }`}
              >
                {watchListLoading ? (
                  <div className="animate-spin h-4 w-4 border border-border-strong border-t-amber-500 rounded-full" />
                ) : isInWatchList ? (
                  <>
                    <CheckIcon className="h-4 w-4" /> In Watch List
                  </>
                ) : (
                  <>
                    <EyeIcon className="h-4 w-4" /> Add to Watch List
                  </>
                )}
              </button>
              
              {/* Pipeline Stage Selector */}
              {isInPipeline && pipelineEntry && (
                <Link
                  to={`/pursuits/${pipelineEntry.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border-strong rounded-lg text-content hover:bg-surface-sunk font-medium"
                >
                  Open workspace
                </Link>
              )}
              {isInPipeline && pipelineEntry ? (
                <div className="relative" ref={stageMenuRef}>
                  <button
                    onClick={() => setShowStageMenu(!showStageMenu)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-brand-300 bg-brand-50 text-brand-700 rounded-lg font-medium hover:bg-brand-100"
                  >
                    {pipelineEntry.stage.charAt(0).toUpperCase() + pipelineEntry.stage.slice(1)}
                    <ChevronDownIcon className="h-3.5 w-3.5 text-brand-500" />
                  </button>
                  {showStageMenu && (
                    <div className="absolute right-0 mt-1 w-40 bg-surface rounded-lg shadow-lg ring-1 ring-border z-50 py-1">
                      {['new', 'analyzing', 'qualified', 'proposal', 'submitted', 'awarded', 'lost', 'archived'].map(s => (
                        <button
                          key={s}
                          onClick={() => { handleStageChangeFromDetail(s); setShowStageMenu(false); }}
                          className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-surface-sunk ${
                            pipelineEntry.stage === s ? 'text-brand-700 font-medium bg-brand-50' : 'text-content'
                          }`}
                        >
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleAddToPipeline}
                  disabled={pipelineLoading}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-content-muted hover:text-content border border-border-strong rounded-lg hover:bg-surface-sunk"
                >
                  {pipelineLoading ? (
                    <div className="animate-spin h-4 w-4 border border-border-strong border-t-brand-500 rounded-full" />
                  ) : (
                    <>
                      <StarIcon className="h-4 w-4" /> Add to Pipeline
                    </>
                  )}
                </button>
              )}
              
              <a 
                href={getSamGovUrl()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-content-muted hover:text-content border border-border-strong rounded-lg hover:bg-surface-sunk"
              >
                <LinkIcon className="h-4 w-4" /> View on {sourceName}
              </a>
            </div>
          </div>
        </div>

        {/* Opportunity Title and Status */}
        <div className="bg-surface rounded-lg shadow-sm border border-border px-6 py-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-content leading-tight">
                {opportunity.title || 'Untitled Opportunity'}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  opportunity.active === 'Yes' || 
                  opportunity.status?.toLowerCase() === 'active' || 
                  opportunity.status?.toLowerCase() === 'open'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {opportunity.active === 'Yes' || 
                   opportunity.status?.toLowerCase() === 'active' || 
                   opportunity.status?.toLowerCase() === 'open' ? 'Open' : (opportunity.status || 'Closed')}
                </span>
                {opportunity.solicitationNumber && (
                  <span className="text-sm text-content-muted">
                    Solicitation: <span className="font-mono">{opportunity.solicitationNumber}</span>
                  </span>
                )}
                {opportunity.naicsCode && (
                  <span className="text-sm text-content-muted">
                    NAICS: <span className="font-mono">{opportunity.naicsCode}</span>
                  </span>
                )}
              </div>
            </div>
            {daysRemaining !== null && (
              <div className={`text-right ${daysRemaining < 7 ? 'text-red-600' : 'text-content-muted'}`}>
                <p className="text-2xl font-bold">{daysRemaining}</p>
                <p className="text-xs">days left</p>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Summary Card - Always visible */}
            <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden">
              <div className="bg-accent-sunk px-4 py-3 border-b border-purple-100">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">AI Contract Analysis</h3>
                </div>
              </div>
              <div className="p-4">
                {loadingSummary ? (
                  <div className="flex items-center gap-3 text-content-muted">
                    <div className="animate-spin h-5 w-5 border border-border-strong border-t-purple-500 rounded-full"></div>
                    <span>Analyzing contract with AI...</span>
                  </div>
                ) : aiSummary ? (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(aiSummary) }} />
                ) : (
                  <p className="text-content-muted text-sm">AI analysis not available for this opportunity.</p>
                )}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <>
                {/* Solicitation Type Badge — auto-classified into the 6-tier taxonomy.
                    Drives downstream agent behavior (different checklists, sections, etc.
                    per type). User can override if classification is wrong. */}
                {opportunity && (
                  <SolicitationTypeBadge
                    opportunityId={opportunity.noticeId || opportunity.id}
                  />
                )}

                {/* Eligibility Gate — deterministic hard-fail detection. Surfaces NAICS
                    mismatch, set-aside ineligibility, expired deadlines, etc. before
                    the user spends time on a proposal. */}
                {opportunity && (
                  <EligibilityGate
                    opportunityId={opportunity.noticeId || opportunity.id}
                  />
                )}

                {/* Overview Tab: Quick Info Card — compact, mobile-first facts grid
                    instead of a narrative run-on sentence that wrapped 15+ lines. */}
                <div className="bg-surface rounded-lg shadow-sm border border-border p-3 sm:p-4">
                  {/* Top row — score (smaller on mobile) + title + build-proposal button */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="flex-shrink-0 flex flex-col items-center">
                      <div className="relative w-14 h-14 sm:w-20 sm:h-20">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                          <path className="text-border" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                          <path className={`${(matchScore ?? 0) >= 70 ? 'text-green-500' : (matchScore ?? 0) >= 40 ? 'text-yellow-500' : 'text-red-400'}`} strokeDasharray={`${matchScore ?? 0}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-base sm:text-xl font-bold text-content">{matchScore !== null ? matchScore : '—'}</span>
                        </div>
                      </div>
                      <p className="text-[10px] sm:text-xs text-center text-content-muted mt-0.5">Match</p>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-content-muted font-semibold">
                        Opportunity
                      </p>
                      <h4
                        className="text-sm sm:text-base font-semibold text-content line-clamp-2 leading-snug"
                        title={typeof opportunity.title === 'string' ? opportunity.title : ''}
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {typeof opportunity.title === 'string' ? opportunity.title : 'Untitled Opportunity'}
                      </h4>
                    </div>

                    {/* Proposal writing is the end of a process, not a peer action to
                        reading the notice: the opportunity has to be in the pipeline
                        first. Hidden until then, and locked by plan the same way the
                        sidebar locks Proposal Writer — this used to be a bare Link that
                        bypassed that lock entirely and went nowhere for free users. */}
                    {isInPipeline && (
                      <div className="flex-shrink-0">
                        {canBuildProposal ? (
                          <Link
                            to={`/proposal-writer?opportunityId=${opportunity.noticeId || opportunity.id}`}
                            className="btn-primary text-xs sm:text-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                            title="Open the Proposal Writer for this opportunity"
                          >
                            <DocumentTextIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            Build Proposal
                          </Link>
                        ) : (
                          <Link
                            to="/pricing"
                            className="btn-secondary text-xs sm:text-sm inline-flex items-center gap-1.5 whitespace-nowrap opacity-80"
                            title="Building proposals requires the Pro plan"
                          >
                            <LockClosedIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            Build Proposal
                          </Link>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Compact facts strip — NAICS lives elsewhere on the page (and in the
                      chat drawer), so we keep this strip tight with just Agency + Set-Aside. */}
                  <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                    <Fact
                      label="Agency"
                      value={formatAgency(opportunity.agency) || '—'}
                      title={opportunity.agency || ''}
                      truncate
                    />
                    <Fact
                      label="Set-Aside"
                      value={
                        opportunity.setAside && opportunity.setAside !== 'None'
                          ? opportunity.setAside
                          : 'Open'
                      }
                    />
                  </div>
                </div>

                {/* AI Insights from Agent Results */}
                {agentResults.length > 0 && (
                  <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden">
                    <div className="bg-accent-sunk px-4 py-3 border-b border-indigo-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <SparklesIcon className="h-5 w-5 text-indigo-600" />
                          <h3 className="font-semibold text-indigo-900">AI Insights</h3>
                          <span className="text-xs text-indigo-500">{agentResults.length} {agentResults.length === 1 ? 'analysis' : 'analyses'}</span>
                        </div>
                        <Link to="/agents" className="text-xs text-indigo-600 hover:text-indigo-800">Run more actions</Link>
                      </div>
                    </div>
                    <div className="divide-y divide-border">
                      {agentResults.slice(0, 5).map((task: any) => {
                        const agentLabels: Record<string, string> = {
                          'contract-scout': 'Opportunity Analysis',
                          'compliance-agent': 'Compliance Check',
                          'vendor-matcher': 'Teaming Partners',
                          'monitor-agent': 'Monitoring',
                          'email-agent': 'Draft Response',
                          'analyst-agent': 'Market Analysis',
                        };
                        const agentColors: Record<string, string> = {
                          'contract-scout': 'bg-blue-100 text-blue-700',
                          'compliance-agent': 'bg-green-100 text-green-700',
                          'vendor-matcher': 'bg-purple-100 text-purple-700',
                          'monitor-agent': 'bg-amber-100 text-amber-700',
                          'email-agent': 'bg-rose-100 text-rose-700',
                          'analyst-agent': 'bg-indigo-100 text-indigo-700',
                        };
                        // One dispatcher decides how to render every agent
                        // payload (see AgentResultView); this used to branch here
                        // and again on the Agents page, and the two drifted.
                        const content = extractAgentText(task.result);

                        const isExpanded = expandedInsight === task.id;
                        const firstLine = content.split('\n').find((l: string) => l.trim().length > 10) || content.substring(0, 80);
                        const summaryText = firstLine.length > 80 ? firstLine.substring(0, 80) + '...' : firstLine;

                        return (
                          <div key={task.id} className="px-4 py-3">
                            {/* Header — always visible, clickable to expand */}
                            <button
                              onClick={() => setExpandedInsight(isExpanded ? null : task.id)}
                              className="w-full text-left"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${agentColors[task.agentType] || 'bg-surface-sunk text-content'}`}>
                                    {agentLabels[task.agentType] || task.agentType}
                                  </span>
                                  <span className="text-xs text-content-subtle">
                                    {new Date(task.completedAt || task.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                </div>
                                {isExpanded
                                  ? <ChevronDownIcon className="h-4 w-4 text-content-subtle" />
                                  : <ChevronRightIcon className="h-4 w-4 text-content-subtle" />
                                }
                              </div>
                              {!isExpanded && (
                                <p className="text-xs text-content-muted mt-1 truncate">{summaryText}</p>
                              )}
                            </button>

                            {/* Full content — shown when expanded */}
                            {isExpanded && (
                              <div className="mt-3">
                                <div className="bg-surface-sunk rounded-lg p-4 text-sm text-content leading-relaxed max-h-[500px] overflow-y-auto border border-border">
                                  <AgentResultView result={task.result} />
                                </div>

                                {/* Quick actions */}
                                {task.agentType === 'contract-scout' && (
                                  <div className="mt-3 p-3 bg-brand-50 border border-brand-200 rounded-lg">
                                    <p className="text-xs font-medium text-brand-800 mb-2">Next steps:</p>
                                    <div className="flex flex-wrap gap-2">
                                      <button onClick={() => runAgentFromDetail('compliance-agent', 'Compliance Check')} className="text-xs px-2.5 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-md font-medium">Run Compliance Check</button>
                                      <button onClick={() => runAgentFromDetail('vendor-matcher', 'Find Partners')} className="text-xs px-2.5 py-1 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-md font-medium">Find Teaming Partners</button>
                                      <button onClick={() => runAgentFromDetail('monitor-agent', 'Monitor Changes')} className="text-xs px-2.5 py-1 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-md font-medium">Monitor Changes</button>
                                      <Link to={`/proposal-writer?opportunityId=${opportunity.noticeId || opportunity.id}`} className="text-xs px-2.5 py-1 bg-brand-100 text-brand-700 hover:bg-brand-200 rounded-md font-medium">Write Proposal</Link>
                                    </div>
                                  </div>
                                )}

                                {task.agentType === 'compliance-agent' && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    <Link to={`/proposal-writer?opportunityId=${opportunity.noticeId || opportunity.id}`} className="text-xs px-2.5 py-1 bg-brand-100 text-brand-700 hover:bg-brand-200 rounded-md font-medium">Write Proposal</Link>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Key Details Grid */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border">
                    <h2 className="text-title text-content">Key Details</h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-content-muted uppercase">Solicitation Number</p>
                        <p className="text-sm font-medium">{opportunity.solicitationNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Type</p>
                        <p className="text-sm font-medium">{opportunity.type || 'Solicitation'}{sourceInfo(opportunity.source) && <span className="ml-2 text-xs font-normal text-content-muted">{sourceInfo(opportunity.source)!.state} · {sourceInfo(opportunity.source)!.system}</span>}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">NAICS</p>
                        <p className="text-sm font-medium">{opportunity.naicsCode || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Place of Performance</p>
                        <p className="text-sm font-medium">
                          {formatLocation(opportunity.placeOfPerformance) || 'Not stated'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Set-Aside</p>
                        <p className="text-sm font-medium">{opportunity.setAside || 'None'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Response Deadline</p>
                        <p className={`text-sm font-medium ${daysRemaining !== null && daysRemaining < 7 ? 'text-red-600' : ''}`}>
                          {formatDate(opportunity.responseDeadline)}
                          {daysRemaining !== null && <span className="ml-2 text-xs">({daysRemaining} days)</span>}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border">
                    <h2 className="text-title text-content">Description</h2>
                  </div>
                  <div className="p-6">
                    {hasMeaningfulDescription(opportunity.description) ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                        <p className="text-slate-700 leading-7 text-base whitespace-pre-wrap">
                          {typeof opportunity.description === 'string' ? opportunity.description : String(opportunity.description)}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                          <DocumentTextIcon className="h-8 w-8 text-amber-600" />
                        </div>
                        <h3 className="text-title text-content mb-2">Description Data Coming Soon</h3>
                        <p className="text-content-muted mb-4 max-w-md mx-auto">
                          We're still processing the full description for this opportunity. 
                          Please check back later or view the details on {sourceName}.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={isInWatchList ? handleRemoveFromWatchList : handleAddToWatchList}
                            disabled={watchListLoading}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                              isInWatchList
                                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                : 'bg-amber-600 text-white hover:bg-amber-700'
                            }`}
                          >
                            {watchListLoading ? (
                              <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full" />
                            ) : isInWatchList ? (
                              <><CheckIcon className="h-4 w-4" /> In Watch List</>
                            ) : (
                              <><EyeIcon className="h-4 w-4" /> Add to Watch List</>
                            )}
                          </button>
                          <a
                            href={getSamGovUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-surface-sunk text-content rounded-lg text-sm font-medium hover:bg-gray-200"
                          >
                            <LinkIcon className="h-4 w-4" /> View on {sourceName}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border">
                    <h2 className="text-title text-content">Contract Documents & Links</h2>
                  </div>
                  <div className="divide-y divide-border">
                    <a 
                      href={getSamGovUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-4 py-3 hover:bg-surface-sunk"
                    >
                      <div className="flex items-center gap-3">
                        <LinkIcon className="h-5 w-5 text-blue-500" />
                        <span className="text-sm text-brand-600 hover:underline">View Opportunity on {sourceName}</span>
                      </div>
                      <ArrowDownTrayIcon className="h-4 w-4 text-content-subtle" />
                    </a>
                    
                    {/* Resource Links from API */}
                    {resourceLinks.length > 0 ? (
                      resourceLinks.map((link: any, index: number) => {
                        const url = normalizeUrl(link);
                        if (!url) return null;
                        const filename = getDocumentNameFromUrl(url);
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`res-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-surface-sunk"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                              title={url}
                            >
                              <DocumentIcon className="h-5 w-5 text-red-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-surface-sunk transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-content-subtle hover:text-brand-600' : 'text-border cursor-not-allowed'
                                }`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-surface-sunk text-content-subtle hover:text-content-muted"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* Links from database */}
                    {documentLinks.length > 0 ? (
                      documentLinks.map((link: any, index: number) => {
                        const url = normalizeUrl(link);
                        if (!url) return null;
                        const filename = getDocumentNameFromUrl(url);
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`link-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-surface-sunk"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                              title={url}
                            >
                              <DocumentIcon className="h-5 w-5 text-blue-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-surface-sunk transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-content-subtle hover:text-brand-600' : 'text-border cursor-not-allowed'
                                }`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-surface-sunk text-content-subtle hover:text-content-muted"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* Attachments */}
                    {opportunity.attachments && opportunity.attachments.length > 0 ? (
                      opportunity.attachments.map((doc: any, index: number) => {
                        const url = doc.url || '#';
                        const filename = typeof doc?.name === 'string' ? doc.name : `Attachment ${index + 1}`;
                        const isSaved = savedDocuments.has(url);
                        return (
                          <div 
                            key={`att-${index}`}
                            className="flex items-center justify-between px-4 py-3 hover:bg-surface-sunk"
                          >
                            <a 
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 flex-1"
                            >
                              <DocumentIcon className="h-5 w-5 text-orange-500" />
                              <span className="text-sm text-brand-600 hover:underline">{filename}</span>
                            </a>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleSaveDocument(url, filename)}
                                disabled={savingDocument === url || isSaved || url === '#' || !isInPipeline}
                                className={`p-1.5 rounded hover:bg-surface-sunk transition-colors ${
                                  isSaved ? 'text-green-600' : isInPipeline ? 'text-content-subtle hover:text-brand-600' : 'text-border cursor-not-allowed'
                                } ${url === '#' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={
                                  isSaved 
                                    ? 'Saved to AI Document Hub' 
                                    : !isInPipeline 
                                      ? 'Add to Pipeline to save documents' 
                                      : 'Save to AI Document Hub'
                                }
                              >
                                <BookmarkIcon className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded hover:bg-surface-sunk text-content-subtle hover:text-content-muted"
                                title="Download"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                    
                    {/* No documents message */}
                    {resourceLinks.length === 0 && documentLinks.length === 0 && (!opportunity.attachments || opportunity.attachments.length === 0) && (
                      <div className="px-4 py-3 text-sm text-content-muted">No additional documents available. Visit {sourceName} for full documentation.</div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'general' && (
              <>
                {/* Agency Information */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border bg-surface-sunk">
                    <h2 className="text-title text-content flex items-center gap-2">
                      <BuildingOfficeIcon className="h-5 w-5 text-brand-600" />
                      Agency Information
                    </h2>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <p className="text-xs text-content-muted uppercase">Department</p>
                      <p className="text-sm font-medium" title={opportunity.agency || ''}>{formatAgency(opportunity.agency) || 'N/A'}</p>
                    </div>
                    {opportunity.subTier && (
                      <div>
                        <p className="text-xs text-content-muted uppercase">Sub-Tier</p>
                        <p className="text-sm font-medium">{opportunity.subTier}</p>
                      </div>
                    )}
                    {opportunity.subAgency && (
                      <div>
                        <p className="text-xs text-content-muted uppercase">Sub-Agency</p>
                        <p className="text-sm font-medium">{opportunity.subAgency}</p>
                      </div>
                    )}
                    {opportunity.office && (
                      <div>
                        <p className="text-xs text-content-muted uppercase">Office</p>
                        <p className="text-sm font-medium">{opportunity.office}</p>
                      </div>
                    )}
                    {opportunity.fpdsCode && (
                      <div>
                        <p className="text-xs text-content-muted uppercase">FPDS Code</p>
                        <p className="text-sm font-medium">{opportunity.fpdsCode}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Place of Performance */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border bg-surface-sunk">
                    <h2 className="text-title text-content flex items-center gap-2">
                      <MapPinIcon className="h-5 w-5 text-brand-600" />
                      Place of Performance
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-content-muted uppercase">City</p>
                        <p className="text-sm font-medium">{formatLocation({ city: opportunity.placeOfPerformance?.city }) || 'Not stated'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">State</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.state || 'Not stated'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">ZIP</p>
                        <p className="text-sm font-medium">{formatLocation({ zip: opportunity.placeOfPerformance?.zip }, { zip: true }) || 'Not stated'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Country</p>
                        <p className="text-sm font-medium">{opportunity.placeOfPerformance?.country || 'United States'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border bg-surface-sunk">
                    <h2 className="text-title text-content flex items-center gap-2">
                      <UserIcon className="h-5 w-5 text-brand-600" />
                      Point of Contact
                    </h2>
                  </div>
                  <div className="p-4">
                    {opportunity.primaryContactName || opportunity.primaryContactEmail ? (
                      <div className="space-y-3">
                        {opportunity.primaryContactName && (
                          <div>
                            <p className="text-xs text-content-muted uppercase">Name</p>
                            <p className="text-sm font-medium">{opportunity.primaryContactName}</p>
                          </div>
                        )}
                        {opportunity.primaryContactEmail && (
                          <div>
                            <p className="text-xs text-content-muted uppercase">Email</p>
                            <a href={`mailto:${opportunity.primaryContactEmail}`} className="text-sm font-medium text-brand-600 hover:underline">
                              {opportunity.primaryContactEmail}
                            </a>
                          </div>
                        )}
                        {opportunity.primaryContactPhone && (
                          <div>
                            <p className="text-xs text-content-muted uppercase">Phone</p>
                            <p className="text-sm font-medium">{opportunity.primaryContactPhone}</p>
                          </div>
                        )}
                      </div>
                    ) : opportunity.pointOfContact && opportunity.pointOfContact.length > 0 ? (
                      <div className="space-y-3">
                        {opportunity.pointOfContact.map((contact: any, idx: number) => (
                          <div key={idx} className="border-b border-border pb-3 last:border-0">
                            {contact.fullName && <p className="font-medium">{contact.fullName}</p>}
                            {contact.title && <p className="text-sm text-content-muted">{contact.title}</p>}
                            {contact.email && <a href={`mailto:${contact.email}`} className="text-sm text-brand-600 hover:underline">{contact.email}</a>}
                            {contact.phone && <p className="text-sm text-content-muted">{contact.phone}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-content-muted italic">Contact information not available. Visit {sourceName} for details.</p>
                    )}
                  </div>
                </div>

                {/* Contract Details */}
                <div className="bg-surface rounded-lg shadow-sm border border-border">
                  <div className="px-4 py-3 border-b border-border bg-surface-sunk">
                    <h2 className="text-title text-content flex items-center gap-2">
                      <DocumentTextIcon className="h-5 w-5 text-brand-600" />
                      Contract Details
                    </h2>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-content-muted uppercase">Posted Date</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.postedDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Response Deadline</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.responseDeadline)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Archive Date</p>
                        <p className="text-sm font-medium">{formatDate(opportunity.archiveDate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Classification Code</p>
                        <p className="text-sm font-medium">{opportunity.classificationCode || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Organization Type</p>
                        <p className="text-sm font-medium">{opportunity.organizationType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-content-muted uppercase">Status</p>
                        <p className="text-sm font-medium">{opportunity.status || 'Open'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'compliance' && (
              <div className="space-y-6">
                {/* Overall Score */}
                <div className="bg-surface rounded-lg shadow-sm border border-border p-6">
                  <h2 className="text-xl font-semibold text-content mb-4">Match Analysis</h2>
                  <div className="flex items-center gap-6 mb-6">
                    <div className="relative w-32 h-32 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                        <path className="text-border" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                        <path className={`${(matchScore ?? 0) >= 70 ? 'text-green-500' : (matchScore ?? 0) >= 40 ? 'text-yellow-500' : 'text-red-400'}`} strokeDasharray={`${matchScore ?? 0}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-3xl font-bold text-content">{matchScore !== null ? matchScore : '—'}</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium text-content text-lg">Overall Score</h3>
                      <p className="text-sm text-content-muted mt-1">
                        Combined from opportunity quality, your profile match, and your activity patterns.
                      </p>
                      {matchWeights && (
                        <p className="text-xs text-content-subtle mt-2">
                          Weights: Quality {matchWeights.quality}% &middot; Profile {matchWeights.profileMatch}% &middot; Behavior {matchWeights.behavioralFit}%
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 3-Pillar Breakdown */}
                {matchPillars && (
                  <div className="grid grid-cols-3 gap-4">
                    {/* Opportunity Quality */}
                    <div className="bg-surface rounded-lg shadow-sm border border-border p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <SparklesIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <h4 className="font-medium text-content text-sm">Opportunity Quality</h4>
                      </div>
                      <p className="text-3xl font-bold text-content">{matchPillars.quality}</p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className={`h-1.5 rounded-full ${matchPillars.quality >= 70 ? 'bg-blue-500' : matchPillars.quality >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${matchPillars.quality}%` }} />
                      </div>
                      <p className="text-xs text-content-muted mt-2">Deadline, description, contacts, scope clarity</p>
                    </div>

                    {/* Profile Match */}
                    <div className="bg-surface rounded-lg shadow-sm border border-border p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <UserIcon className="h-4 w-4 text-purple-600" />
                        </div>
                        <h4 className="font-medium text-content text-sm">Profile Match</h4>
                      </div>
                      <p className="text-3xl font-bold text-content">{matchPillars.profileMatch}</p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className={`h-1.5 rounded-full ${matchPillars.profileMatch >= 70 ? 'bg-purple-500' : matchPillars.profileMatch >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${matchPillars.profileMatch}%` }} />
                      </div>
                      <p className="text-xs text-content-muted mt-2">NAICS, certifications, location, company size</p>
                    </div>

                    {/* Behavioral Fit */}
                    <div className="bg-surface rounded-lg shadow-sm border border-border p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <ChartBarIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <h4 className="font-medium text-content text-sm">Behavioral Fit</h4>
                      </div>
                      <p className="text-3xl font-bold text-content">{matchPillars.behavioralFit}</p>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className={`h-1.5 rounded-full ${matchPillars.behavioralFit >= 70 ? 'bg-green-500' : matchPillars.behavioralFit >= 40 ? 'bg-yellow-500' : 'bg-red-400'}`} style={{ width: `${matchPillars.behavioralFit}%` }} />
                      </div>
                      <p className="text-xs text-content-muted mt-2">Based on opportunities you've viewed and pursued</p>
                    </div>
                  </div>
                )}

                {/* Signals & Reasons */}
                <div className="bg-surface rounded-lg shadow-sm border border-border p-6">
                  <h3 className="font-medium text-content mb-4">Scoring Signals</h3>
                  <div className="space-y-3">
                    {matchReasons.length > 0 ? (
                      matchReasons.map((reason, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            reason.toLowerCase().includes('ineligible') || reason.toLowerCase().includes('passed') || reason.toLowerCase().includes('no ')
                              ? 'bg-red-100'
                              : reason.toLowerCase().includes('low') || reason.toLowerCase().includes('brief') || reason.toLowerCase().includes('less than')
                                ? 'bg-yellow-100'
                                : 'bg-green-100'
                          }`}>
                            <span className={`text-xs ${
                              reason.toLowerCase().includes('ineligible') || reason.toLowerCase().includes('passed') || reason.toLowerCase().includes('no ')
                                ? 'text-red-600'
                                : reason.toLowerCase().includes('low') || reason.toLowerCase().includes('brief') || reason.toLowerCase().includes('less than')
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                            }`}>
                              {reason.toLowerCase().includes('ineligible') || reason.toLowerCase().includes('passed') || reason.toLowerCase().includes('no ') ? '!' :
                               reason.toLowerCase().includes('low') || reason.toLowerCase().includes('brief') || reason.toLowerCase().includes('less than') ? '!' : '\u2713'}
                            </span>
                          </div>
                          <p className="text-sm text-content">{reason}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-content-muted">
                        No specific signals yet. Browse more opportunities to build your behavioral profile, or add NAICS codes in Settings to improve profile matching.
                      </p>
                    )}
                  </div>
                </div>

                {/* Score Improvement Hints */}
                {opportunity && user && (
                  <div className="bg-surface rounded-lg shadow-sm border border-border p-6">
                    <MatchScoreHints
                      opportunity={{
                        setAside: (opportunity as any).setAside,
                        typeOfSetAside: (opportunity as any).typeOfSetAside,
                        naicsCodes: (opportunity as any).naicsCodes,
                        placeOfPerformance: (opportunity as any).placeOfPerformance,
                      }}
                      user={{
                        certifications: (user as any).certifications,
                        naicsCodes: (user as any).naicsCodes,
                        companyState: (user as any).companyState,
                      }}
                      onProfileUpdated={() => {
                        // Re-fetch match score after profile update
                        if (opportunity?.id) {
                          opportunityApi.getById(opportunity.id).then(() => {}).catch(() => {});
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - AI Chat */}
          <div className={`transition-all duration-300 ${isChatExpanded ? 'hidden' : ''} sticky top-6`}>
            <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden" style={{ height: 'calc(100vh - 200px)', maxHeight: '800px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
              <div style={{ flexShrink: 0 }} className="px-4 py-3 border-b border-border bg-accent-sunk">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600" />
                    <div>
                      <h3 className="font-semibold text-purple-900">Your AI Contracting Consultant</h3>
                      <p className="text-xs text-purple-700 flex items-center gap-1">
                        Ask me anything about this contract
                        {isChatConnected && (
                          <span className="inline-flex items-center gap-1 text-green-600" title="WebSocket connected - Real-time streaming">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-[10px]">Live</span>
                          </span>
                        )}
                        {!isChatConnected && chatTransport === 'sse' && (
                          <span className="inline-flex items-center gap-1 text-blue-600" title="Using SSE streaming">
                            <CloudIcon className="h-3 w-3" />
                            <span className="text-[10px]">SSE</span>
                          </span>
                        )}
                        {chatMessages.length > 0 && chatMessages[chatMessages.length - 1]?.metadata?.usedFallback && (
                          <span className="inline-flex items-center gap-1 text-amber-600" title="Using backup AI (OpenRouter)">
                            <span className="text-[10px]">Fallback AI</span>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {creditsTotal > 0 && (
                      <div className="text-xs text-purple-700 mr-2">
                        {creditsUsed}/{creditsTotal} credits
                      </div>
                    )}
                    {/* Expand Button */}
                    <button
                      onClick={() => setIsChatExpanded(true)}
                      className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Expand chat for full view"
                    >
                      <ArrowsPointingOutIcon className="h-4 w-4" />
                    </button>
                    {/* History Toggle Button */}
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        showHistory 
                          ? 'bg-purple-200 text-purple-800' 
                          : 'text-purple-600 hover:bg-purple-100'
                      }`}
                      title="Conversation history"
                    >
                      <ClockIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Sample Questions — collapsible, amber header so it contrasts
                  with the blue Specialist bar. Auto-expanded when there's no
                  chat history, auto-collapsed after the first message. */}
              {isInPipeline && (
                <div style={{ flexShrink: 0 }} className="border-b border-border">
                  <button
                    onClick={() => setIsSamplesExpanded((v) => !v)}
                    className="w-full px-4 py-2 flex items-center justify-between gap-2 bg-amber-50 hover:bg-amber-100 border-b border-amber-100 transition-colors text-left"
                    aria-expanded={isSamplesExpanded}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        Sample Questions
                      </span>
                      <span className="text-xs text-amber-900 font-medium">
                        {SAMPLE_QUESTIONS.length} to get started
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 text-amber-700">
                      <span className="text-[10px] font-medium uppercase tracking-wider">
                        {isSamplesExpanded ? 'Hide' : 'Show'}
                      </span>
                      {isSamplesExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </div>
                  </button>
                  {isSamplesExpanded && (
                    <div className="p-3 bg-surface-sunk max-h-40 overflow-y-auto">
                      <div className="space-y-1">
                        {SAMPLE_QUESTIONS.map((q, i) => (
                          <button
                            key={i}
                            onClick={() => handleSampleQuestion(q)}
                            className="block w-full text-left text-xs text-content hover:text-amber-700 hover:bg-amber-50 px-2 py-1.5 rounded border border-transparent hover:border-amber-200 transition-colors"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Pipeline Required Notice (when no messages) */}
              {chatMessages.length === 0 && !isInPipeline && (
                <div style={{ flexShrink: 0 }} className="p-6 border-b border-border bg-surface-sunk text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-100 mb-3">
                    <ChatBubbleLeftRightIcon className="h-6 w-6 text-brand-600" />
                  </div>
                  <h4 className="text-sm font-medium text-content mb-1">AI Consultant</h4>
                  <p className="text-xs text-content-muted mb-3">
                    Ask questions about requirements, compliance, pricing, and more.
                  </p>
                  <button
                    onClick={handleAddToPipeline}
                    disabled={pipelineLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
                  >
                    {pipelineLoading ? (
                      <>
                        <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <StarIcon className="h-3 w-3" />
                        Add to Pipeline to Start
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Topics - Agent Routing (collapsible) */}
              <div style={{ flexShrink: 0 }} className="border-b border-border">
                <button
                  onClick={() => setIsTopicsExpanded((v) => !v)}
                  className="w-full px-4 py-2 flex items-center justify-between gap-2 bg-brand-50 hover:bg-brand-100 border-b border-brand-100 transition-colors text-left"
                  aria-expanded={isTopicsExpanded}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-semibold text-brand-700 uppercase tracking-wide">
                      Specialist
                    </span>
                    <span className="text-xs text-brand-900 font-medium truncate">
                      {TOPICS.find((t) => t.id === selectedTopic)?.label ?? 'General Information'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 text-brand-700">
                    <span className="text-[10px] font-medium uppercase tracking-wider">
                      {isTopicsExpanded ? 'Hide' : 'Change'}
                    </span>
                    {isTopicsExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </div>
                </button>
                {isTopicsExpanded && (
                  <div className="px-4 py-2 bg-surface-sunk">
                    <p className="text-xs text-content-muted mb-2">Select topic for specialized agent:</p>
                    <div className="flex flex-wrap gap-1">
                      {TOPICS.map((topic) => (
                        <button
                          key={topic.id}
                          onClick={() => setSelectedTopic(topic.id)}
                          className={`
                            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors
                            ${selectedTopic === topic.id
                              ? 'bg-brand-100 text-brand-700 border border-brand-300'
                              : 'bg-surface text-content-muted border border-border hover:bg-surface-sunk'
                            }
                          `}
                          title={`Route to ${topic.label} specialist`}
                        >
                          <topic.icon className="h-3 w-3" />
                          {topic.label}
                        </button>
                      ))}
                    </div>
                    {selectedTopic !== 'general' && (
                      <p className="mt-2 text-xs text-brand-600">
                        Using {TOPICS.find(t => t.id === selectedTopic)?.label} specialist
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Chat Content - Messages + Input Wrapper */}
              <div style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Chat Messages */}
                <div style={{ flex: '1 1 0%', minHeight: 0, overflowY: 'auto' }} className="p-4 space-y-4">
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`flex w-full min-w-0 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] min-w-0 rounded-lg px-4 py-3 text-sm break-words ${
                        msg.role === 'user'
                          ? 'bg-brand-600 text-white'
                          : 'bg-surface-sunk text-content border border-border'
                      }`}
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {msg.role === 'user' ? (
                        <span style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</span>
                      ) : (
                        <div className="markdown-body prose prose-sm max-w-none" style={{ maxWidth: '100%' }}>
                          <MarkdownContent content={typeof msg.content === 'string' ? msg.content : String(msg.content || '')} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-surface-sunk rounded-lg px-3 py-2 flex items-center gap-2">
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full"></div>
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full delay-100"></div>
                      <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full delay-200"></div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div style={{ flexShrink: 0 }} className="p-4 border-t border-border">
                {chatError && (
                  <div className="mb-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                    {chatError}
                  </div>
                )}
                
                {/* Pipeline Requirement Notice */}
                {!isInPipeline && (
                  <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 text-center">
                      <span className="font-medium">AI Consultant requires Pipeline access.</span>
                      <button
                        onClick={handleAddToPipeline}
                        disabled={pipelineLoading}
                        className="ml-1 underline hover:text-amber-900"
                      >
                        {pipelineLoading ? 'Adding...' : 'Add to Pipeline'}
                      </button>
                    </p>
                  </div>
                )}
                
                {/* Voice Error */}
                {voiceError && isVoiceSupported && (
                  <div className="mb-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    {voiceError}
                  </div>
                )}
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput + (isListening && interimTranscript ? ' ' + interimTranscript : '')}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder={!isInPipeline 
                      ? "Add to Pipeline to use AI Consultant..." 
                      : isListening
                        ? "Listening..."
                        : isChatLoading 
                          ? "Thinking..." 
                          : "Type your question or use voice..."
                    }
                    disabled={isChatLoading || !isInPipeline}
                    className="flex-1 px-3 py-2 border border-border-strong rounded-lg text-sm focus:ring-2 focus:ring-brand-500 disabled:bg-surface-sunk"
                  />
                  
                  {/* Voice Input Button */}
                  {isVoiceSupported && isInPipeline && (
                    <button
                      onClick={isListening ? handleVoiceSend : startListening}
                      disabled={isChatLoading}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        isListening 
                          ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                          : 'bg-surface-sunk text-content-muted hover:bg-gray-200'
                      } disabled:opacity-50`}
                      title={isListening ? 'Click to send voice message' : 'Use voice input'}
                    >
                      {isListening ? (
                        <StopIcon className="h-4 w-4" />
                      ) : (
                        <MicrophoneIcon className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  
                  <button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || isChatLoading || !isInPipeline}
                    className="px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                    title={!isInPipeline ? "Add to Pipeline to use AI Consultant" : "Send message"}
                  >
                    <PaperAirplaneIcon className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-content-muted">
                  <span>
                    {isChatConnected ? '● WebSocket' : chatTransport === 'sse' ? '○ SSE' : '○ Connecting...'} 
                    {' • '}
                    {isChatLoading ? 'Thinking...' : 'Ready'}
                  </span>
                  <span>Uses {creditsUsed}/{creditsTotal} credits</span>
                </div>
              </div>
            </div>
            
          </div>
          <ConversationHistory
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
            onSelect={(conversation) => {
              loadConversation(conversation.id);
              setShowHistory(false);
            }}
            onDeleted={handleConversationDeleted}
            currentConversationId={chatConversationId}
            opportunityId={opportunity?.id}
          />
        </div>
      </div>

      {/* Expanded Chat Drawer Overlay */}
      {isChatExpanded && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
            onClick={() => setIsChatExpanded(false)}
          />
          
          {/* Drawer Panel */}
          <div className="relative w-full sm:ml-auto sm:max-w-4xl h-full bg-white shadow-2xl animate-in slide-in-from-right duration-300 overflow-hidden" style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <div style={{ flexShrink: 0 }} className="px-6 py-4 border-b border-border bg-accent-sunk flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-purple-900">Your Digital Contracting Consultant</h2>
                  <p className="text-sm text-purple-700 flex items-center gap-2">
                    <span>{opportunity?.title || 'Opportunity'}</span>
                    {isChatConnected && (
                      <span className="inline-flex items-center gap-1 text-green-600 text-xs" title="WebSocket connected">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Live
                      </span>
                    )}
                    {!isChatConnected && chatTransport === 'sse' && (
                      <span className="inline-flex items-center gap-1 text-blue-600 text-xs">
                        <CloudIcon className="h-3 w-3" />
                        SSE
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {creditsTotal > 0 && (
                  <div className="px-3 py-1 bg-purple-100 rounded-full text-sm text-purple-700">
                    {creditsUsed}/{creditsTotal} credits
                  </div>
                )}
                {/* History Button */}
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className={`p-2 rounded-lg transition-colors ${
                    showHistory 
                      ? 'bg-purple-200 text-purple-800' 
                      : 'text-purple-600 hover:bg-purple-100'
                  }`}
                  title="Conversation history"
                >
                  <ClockIcon className="h-5 w-5" />
                </button>
                {/* Collapse Button */}
                <button
                  onClick={() => setIsChatExpanded(false)}
                  className="p-2 rounded-lg text-content-muted hover:bg-surface-sunk transition-colors"
                  title="Collapse to sidebar"
                >
                  <ArrowsPointingInIcon className="h-5 w-5" />
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setIsChatExpanded(false)}
                  className="p-2 rounded-lg text-content-muted hover:bg-surface-sunk hover:text-red-600 transition-colors"
                  title="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Quick Opportunity Summary Bar — compact, single-line on mobile so
                the chat area gets back the real estate the metadata used to eat. */}
            <div
              style={{ flexShrink: 0 }}
              className="px-4 sm:px-6 py-1.5 border-b border-border bg-surface-sunk flex items-center gap-3 sm:gap-6 text-xs sm:text-sm overflow-hidden"
            >
              <div className="flex items-center gap-1.5 min-w-0 flex-shrink">
                <span className="text-content-muted hidden sm:inline shrink-0">Agency:</span>
                <span
                  className="font-medium text-content truncate"
                  title={opportunity?.agency || ''}
                >
                  {formatAgency(opportunity?.agency) || 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-content-muted">NAICS:</span>
                <span className="font-medium text-content font-mono">{opportunity?.naicsCode || '—'}</span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-content-muted hidden sm:inline">Deadline:</span>
                <span
                  className={`font-medium ${
                    daysRemaining !== null && daysRemaining < 7 ? 'text-red-600' : 'text-content'
                  }`}
                >
                  {formatDate(opportunity?.responseDeadline)}
                </span>
              </div>
              <Link
                to={`/opportunities/${id}`}
                onClick={() => setIsChatExpanded(false)}
                className="ml-auto text-brand-600 hover:text-brand-700 flex items-center gap-1 shrink-0"
                title="Back to full opportunity details"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Back to Details</span>
              </Link>
            </div>

            {/* Topics - Agent Routing (collapsible) */}
            <div style={{ flexShrink: 0 }} className="border-b border-border">
              <button
                onClick={() => setIsTopicsExpanded((v) => !v)}
                className="w-full px-6 py-2 flex items-center justify-between gap-2 bg-brand-50 hover:bg-brand-100 border-b border-brand-100 transition-colors text-left"
                aria-expanded={isTopicsExpanded}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-semibold text-brand-700 uppercase tracking-wide">
                    Specialist
                  </span>
                  <span className="text-sm text-brand-900 font-medium truncate">
                    {TOPICS.find((t) => t.id === selectedTopic)?.label ?? 'General Information'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-brand-700">
                  <span className="text-[11px] font-medium uppercase tracking-wider">
                    {isTopicsExpanded ? 'Hide' : 'Change'}
                  </span>
                  {isTopicsExpanded ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </div>
              </button>
              {isTopicsExpanded && (
                <div className="px-6 py-3 bg-surface-sunk">
                  <p className="text-xs text-content-muted mb-2">Select topic for specialized agent:</p>
                  <div className="flex flex-wrap gap-2">
                    {TOPICS.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => setSelectedTopic(topic.id)}
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                          ${selectedTopic === topic.id
                            ? 'bg-brand-100 text-brand-700 border border-brand-300 shadow-sm'
                            : 'bg-surface text-content-muted border border-border hover:bg-surface-sunk'
                          }
                        `}
                        title={`Route to ${topic.label} specialist`}
                      >
                        <topic.icon className="h-4 w-4" />
                        {topic.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sample Questions (drawer) — same amber collapsible as inline view */}
            {isInPipeline && (
              <div style={{ flexShrink: 0 }} className="border-b border-border">
                <button
                  onClick={() => setIsSamplesExpanded((v) => !v)}
                  className="w-full px-6 py-2 flex items-center justify-between gap-2 bg-amber-50 hover:bg-amber-100 border-b border-amber-100 transition-colors text-left"
                  aria-expanded={isSamplesExpanded}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                      Sample Questions
                    </span>
                    <span className="text-sm text-amber-900 font-medium">
                      {SAMPLE_QUESTIONS.length} to get started
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 text-amber-700">
                    <span className="text-[11px] font-medium uppercase tracking-wider">
                      {isSamplesExpanded ? 'Hide' : 'Show'}
                    </span>
                    {isSamplesExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </div>
                </button>
                {isSamplesExpanded && (
                  <div className="px-6 py-3 bg-surface-sunk max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {SAMPLE_QUESTIONS.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => handleSampleQuestion(q)}
                          className="text-left text-sm text-content hover:text-amber-700 hover:bg-amber-50 bg-surface border border-border hover:border-amber-200 rounded-lg px-3 py-2 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Chat Content Area - Messages + Input */}
            <div style={{ flex: '1 1 0%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Chat Messages - Expanded */}
              <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6 bg-surface-sunk">
                {chatMessages.length === 0 && isInPipeline && (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mb-4">
                      <ChatBubbleLeftRightIcon className="h-10 w-10 text-purple-600" />
                    </div>
                    <h3 className="text-xl font-medium text-content mb-2">How can I help you today?</h3>
                    <p className="text-content-muted max-w-md mx-auto">
                      Ask anything about this opportunity — requirements, compliance, pricing,
                      subcontracting, or proposal writing. Pick a sample question above to get started.
                    </p>
                  </div>
                )}
                {chatMessages.map((msg, index) => (
                  <div key={index} className={`flex w-full min-w-0 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] min-w-0 rounded-xl px-6 py-4 shadow-sm break-words ${
                        msg.role === 'user'
                          ? 'bg-brand-600 text-white'
                          : 'bg-surface text-content border border-border'
                      }`}
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {msg.role === 'user' ? (
                        <p className="text-base leading-relaxed" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{msg.content}</p>
                      ) : (
                        <div className="markdown-body prose prose-base max-w-none" style={{ maxWidth: '100%' }}>
                          <MarkdownContent content={typeof msg.content === 'string' ? msg.content : String(msg.content || '')} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="bg-surface rounded-xl px-6 py-4 flex items-center gap-3 shadow-sm border border-border">
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full"></div>
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full delay-100"></div>
                      <div className="animate-bounce h-3 w-3 bg-gray-400 rounded-full delay-200"></div>
                      <span className="text-sm text-content-muted ml-2">Consultant is thinking...</span>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input - Expanded */}
              <div style={{ flexShrink: 0 }} className="px-4 py-3 sm:p-6 border-t border-border bg-surface">
              {chatError && (
                <div className="mb-3 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                  {chatError}
                </div>
              )}
              
              {!isInPipeline && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 text-center">
                    <span className="font-medium">AI Consultant requires Pipeline access.</span>
                    <button
                      onClick={handleAddToPipeline}
                      disabled={pipelineLoading}
                      className="ml-2 underline hover:text-amber-900"
                    >
                      {pipelineLoading ? 'Adding...' : 'Add to Pipeline'}
                    </button>
                  </p>
                </div>
              )}
              
              {voiceError && isVoiceSupported && (
                <div className="mb-3 text-sm text-amber-600 bg-amber-50 px-4 py-2 rounded">
                  {voiceError}
                </div>
              )}
              
              <div className="flex gap-2 sm:gap-3">
                <input
                  type="text"
                  value={chatInput + (isListening && interimTranscript ? ' ' + interimTranscript : '')}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={!isInPipeline
                    ? "Add to Pipeline to use AI Consultant..."
                    : isListening
                      ? "Listening..."
                      : isChatLoading
                        ? "Thinking..."
                        : "Type your question..."
                  }
                  disabled={isChatLoading || !isInPipeline}
                  className="flex-1 min-w-0 px-3 sm:px-4 py-3 border border-border-strong rounded-xl text-base focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-surface-sunk transition-all"
                />
                
                {isVoiceSupported && isInPipeline && (
                  <button
                    onClick={isListening ? handleVoiceSend : startListening}
                    disabled={isChatLoading}
                    className={`px-4 py-3 rounded-xl transition-colors ${
                      isListening 
                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' 
                        : 'bg-surface-sunk text-content-muted hover:bg-gray-200'
                    } disabled:opacity-50`}
                    title={isListening ? 'Click to send voice message' : 'Use voice input'}
                  >
                    {isListening ? (
                      <StopIcon className="h-5 w-5" />
                    ) : (
                      <MicrophoneIcon className="h-5 w-5" />
                    )}
                  </button>
                )}
                
                <button
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim() || isChatLoading || !isInPipeline}
                  className="px-4 sm:px-6 py-3 bg-brand-600 text-white rounded-xl hover:bg-brand-700 disabled:opacity-50 flex items-center gap-2 font-medium transition-all flex-shrink-0"
                  title={!isInPipeline ? "Add to Pipeline to use AI Consultant" : "Send message"}
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                  Send
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-content-muted">
                <span className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 ${isChatConnected ? 'text-green-600' : 'text-content-subtle'}`}>
                    {isChatConnected ? '● WebSocket' : chatTransport === 'sse' ? '○ SSE' : '○ Connecting...'} 
                  </span>
                  <span>•</span>
                  <span>{isChatLoading ? 'Consultant is thinking...' : 'Ready'}</span>
                </span>
                <span>Uses {creditsUsed}/{creditsTotal} credits</span>
              </div>
            </div>
            </div>

            <ConversationHistory
              isOpen={showHistory}
              onClose={() => setShowHistory(false)}
              onSelect={(conversation) => {
                loadConversation(conversation.id);
                setShowHistory(false);
              }}
              currentConversationId={chatConversationId}
              opportunityId={opportunity?.id}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Markdown table styles
const markdownStyles = `
.markdown-body table {
  border-collapse: collapse;
  width: 100%;
  margin: 1rem 0;
}
.markdown-body th,
.markdown-body td {
  border: 1px solid #d1d5db;
  padding: 0.75rem;
  text-align: left;
}
.markdown-body th {
  background-color: #f3f4f6;
  font-weight: 600;
}
.markdown-body tr:nth-child(even) {
  background-color: #f9fafb;
}
.markdown-body h1 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 1.5rem 0 1rem;
}
.markdown-body h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 1.25rem 0 0.75rem;
}
.markdown-body h3 {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 1rem 0 0.5rem;
}
.markdown-body p {
  margin: 0.75rem 0;
}
.markdown-body ul,
.markdown-body ol {
  margin: 0.75rem 0;
  padding-left: 1.5rem;
}
.markdown-body li {
  margin: 0.25rem 0;
}
.markdown-body strong {
  font-weight: 600;
}
.markdown-body a {
  color: #0ea5e9;
  text-decoration: none;
}
.markdown-body a:hover {
  text-decoration: underline;
}
.markdown-body blockquote {
  border-left: 4px solid #0ea5e9;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #4b5563;
}
.markdown-body code {
  background-color: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.875rem;
}
.markdown-body pre {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1rem 0;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleId = 'markdown-body-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = markdownStyles;
    document.head.appendChild(style);
  }
}

export default OpportunityDetail;

